import React, { useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Text,
  Button,
  Spinner,
  Flex,
  Box,
  VStack,
  Alert,
  AlertIcon,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useDeleteImpact, useHardDeleteTrack } from '../../api/libraryApi';
import InputField from '../../components/fields/InputField';

interface HardDeleteDialogProps {
  trackId: string;
  trackTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

export function HardDeleteDialog({
  trackId,
  trackTitle,
  isOpen,
  onClose,
}: HardDeleteDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [confirmText, setConfirmText] = useState('');
  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const dialogBg = useColorModeValue('white', 'navy.800');
  const curatedBg = useColorModeValue('orange.50', 'orange.900');
  const customerBg = useColorModeValue('red.50', 'red.900');
  const blobBg = useColorModeValue('secondaryGray.300', 'navy.700');

  const { data: impact, isLoading } = useDeleteImpact(trackId, {
    enabled: isOpen && !!trackId,
  });

  const deleteMutation = useHardDeleteTrack();

  const isConfirmMatch = confirmText === trackTitle;

  const handleConfirm = () => {
    deleteMutation.mutate(trackId, {
      onSuccess: () => {
        toast({
          title: t('admin.library.hardDelete.successToast'),
          status: 'success',
          duration: 3000,
        });
        onClose();
        navigate('/library');
      },
      onError: () => {
        toast({
          title: t('admin.library.hardDelete.errorToast'),
          status: 'error',
          duration: 5000,
        });
      },
    });
  };

  const handleClose = () => {
    if (!deleteMutation.isPending) {
      setConfirmText('');
      onClose();
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
      isCentered
    >
      <AlertDialogOverlay />
      <AlertDialogContent
        data-testid="hard-delete-dialog"
        maxW="520px"
        borderRadius="20px"
        bg={dialogBg}
      >
        <AlertDialogHeader pb="8px">
          <Text fontSize="lg" fontWeight="700" color={textColor}>
            {t('admin.library.hardDelete.dialogTitle')}
          </Text>
        </AlertDialogHeader>

        <AlertDialogBody>
          <VStack spacing={4} align="stretch">
            <Alert status="error" borderRadius="16px">
              <AlertIcon />
              <Text fontSize="sm">{t('admin.library.hardDelete.warning')}</Text>
            </Alert>

            {isLoading ? (
              <Flex justify="center" p={4}>
                <Spinner size="md" />
              </Flex>
            ) : impact ? (
              <>
                {impact.totalAffectedPlaylists === 0 ? (
                  <Text color={secondaryColor} fontSize="sm">
                    {t('admin.library.hardDelete.noPlaylistsAffected')}
                  </Text>
                ) : (
                  <>
                    {impact.curatedPlaylists.length > 0 && (
                      <Box>
                        <Text fontWeight="700" fontSize="sm" color={textColor} mb={2}>
                          {t('admin.library.hardDelete.curatedPlaylistsHeading')}
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {impact.curatedPlaylists.map((pl) => (
                            <Flex
                              key={pl.id}
                              p="10px 12px"
                              bg={curatedBg}
                              borderRadius="12px"
                              justify="space-between"
                              align="center"
                            >
                              <Text fontSize="sm" color={textColor} fontWeight="500">
                                {pl.name}
                              </Text>
                              <Badge colorScheme="orange" fontSize="xs" borderRadius="8px">
                                {pl.trackCountAfterRemoval} {t('admin.library.hardDelete.trackCountAfterRemoval')}
                              </Badge>
                            </Flex>
                          ))}
                        </VStack>
                      </Box>
                    )}

                    {impact.customerPlaylists.length > 0 && (
                      <Box>
                        <Text fontWeight="700" fontSize="sm" color={textColor} mb={2}>
                          {t('admin.library.hardDelete.customerPlaylistsHeading')}
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {impact.customerPlaylists.map((pl) => (
                            <Flex
                              key={pl.id}
                              p="10px 12px"
                              bg={customerBg}
                              borderRadius="12px"
                              justify="space-between"
                              align="center"
                            >
                              <Box>
                                <Text fontSize="sm" color={textColor} fontWeight="500">
                                  {pl.name}
                                </Text>
                                <Text fontSize="xs" color={secondaryColor}>
                                  {t('admin.library.hardDelete.organizationLabel')}: {pl.organizationName}
                                </Text>
                              </Box>
                              <Badge colorScheme="red" fontSize="xs" borderRadius="8px">
                                {pl.trackCountAfterRemoval} {t('admin.library.hardDelete.trackCountAfterRemoval')}
                              </Badge>
                            </Flex>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </>
                )}

                {/* Blob files section */}
                <Box>
                  <Text fontWeight="700" fontSize="sm" color={textColor} mb={2}>
                    {t('admin.library.hardDelete.blobFilesHeading')}
                  </Text>
                  <VStack spacing={2} align="stretch">
                    <Flex p="10px 12px" bg={blobBg} borderRadius="12px" justify="space-between">
                      <Text fontSize="sm" color={textColor} fontWeight="500">
                        {t('admin.library.hardDelete.originalFile')}
                      </Text>
                      <Text fontSize="xs" color={secondaryColor}>
                        {impact.blobFiles.originalBlobPath ?? t('admin.library.hardDelete.noFile')}
                      </Text>
                    </Flex>
                    <Flex p="10px 12px" bg={blobBg} borderRadius="12px" justify="space-between">
                      <Text fontSize="sm" color={textColor} fontWeight="500">
                        {t('admin.library.hardDelete.processedFile')}
                      </Text>
                      <Text fontSize="xs" color={secondaryColor}>
                        {impact.blobFiles.processedBlobPath ?? t('admin.library.hardDelete.noFile')}
                      </Text>
                    </Flex>
                  </VStack>
                </Box>

                {/* Type-to-confirm */}
                <Box>
                  <Text fontSize="sm" color={textColor} mb={1}>
                    {t('admin.library.hardDelete.confirmPrompt')}
                  </Text>
                  <Text fontSize="sm" fontWeight="700" color={textColor} mb={2}>
                    {trackTitle}
                  </Text>
                  <InputField
                    id="hard-delete-confirm-input"
                    data-testid="hard-delete-confirm-input"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={trackTitle}
                    isDisabled={deleteMutation.isPending}
                    mb="0"
                  />
                </Box>
              </>
            ) : null}
          </VStack>
        </AlertDialogBody>

        <AlertDialogFooter gap={2}>
          <Button
            ref={cancelRef}
            onClick={handleClose}
            isDisabled={deleteMutation.isPending}
            variant="outline"
            borderRadius="16px"
          >
            {t('admin.library.hardDelete.cancelButton')}
          </Button>
          <Button
            data-testid="hard-delete-confirm-button"
            colorScheme="red"
            onClick={handleConfirm}
            isDisabled={!isConfirmMatch || deleteMutation.isPending || isLoading}
            isLoading={deleteMutation.isPending}
            borderRadius="16px"
          >
            {t('admin.library.hardDelete.confirmButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
